import { DiscountApplyTypeEnum } from "src/enums/order.enum";
import { OrderDiscount } from "src/interfaces/order.interface";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";

interface DiscountRuleDisplayContext {
  appliedPromotionCode?: string | null;
  promotionSnapshotJson?: string | null;
}

function formatPercentageLabel(value: number): string {
  const roundedInt = Math.round(value);
  if (Math.abs(value - roundedInt) < 0.05) return `${roundedInt}% off`;
  return `${value.toFixed(2)}% off`;
}

interface PromotionSnapshotShape {
  promotionType?: string;
  discountValue?: number | string;
  promotionCode?: string;
}

function parsePromotionSnapshot(promotionSnapshotJson: string | null | undefined): PromotionSnapshotShape | null {
  if (promotionSnapshotJson == null || promotionSnapshotJson.trim() === "") return null;
  try {
    return JSON.parse(promotionSnapshotJson) as PromotionSnapshotShape;
  } catch {
    return null;
  }
}

export function formatOrderDiscountRuleDisplay(
  discount: OrderDiscount | null | undefined,
  context?: DiscountRuleDisplayContext
): string {
  if (discount == null) return "Order Discount";
  const reference = discount.discountReference?.trim();
  const reason = discount.discountReason?.trim();
  const discountType = discount.discountType ? String(discount.discountType).toUpperCase() : "";
  const applyType = discount.discountApplyType
    ? String(discount.discountApplyType).toUpperCase()
    : "";
  const snapshot = parsePromotionSnapshot(context?.promotionSnapshotJson);
  const snapshotPromotionType = snapshot?.promotionType ? String(snapshot.promotionType).toUpperCase() : "";
  const snapshotDiscountValue =
    snapshot?.discountValue != null && snapshot.discountValue !== ""
      ? Number(snapshot.discountValue)
      : null;
  const snapshotCode = snapshot?.promotionCode?.trim();
  const codeFromContext = context?.appliedPromotionCode?.trim();
  const promoCode = snapshotCode || codeFromContext || reference || null;

  if (snapshotPromotionType === "ORDER_PERCENT_OFF" && snapshotDiscountValue != null && !Number.isNaN(snapshotDiscountValue)) {
    const snapshotRule = formatPercentageLabel(snapshotDiscountValue);
    if (promoCode) return `${snapshotRule} (Coupon ${promoCode})`;
    return snapshotRule;
  }
  if (snapshotPromotionType === "ORDER_AMOUNT_OFF" && snapshotDiscountValue != null && !Number.isNaN(snapshotDiscountValue)) {
    const amountOffCents = Math.round(Math.abs(snapshotDiscountValue) * 100);
    const snapshotRule = `${formatCentsToDollarsWithDollarSign(amountOffCents)} off`;
    if (promoCode) return `${snapshotRule} (Coupon ${promoCode})`;
    return snapshotRule;
  }
  if (snapshotPromotionType === "FREE_DELIVERY") {
    if (promoCode) return `Free delivery (Coupon ${promoCode})`;
    return "Free delivery";
  }

  const percentRule =
    discount.discountPercentage != null
      ? formatPercentageLabel(Number(discount.discountPercentage))
      : null;
  const fixedRule =
    discount.discountCents != null
      ? `${formatCentsToDollarsWithDollarSign(Math.abs(Number(discount.discountCents)))} off`
      : null;

  // Rule value should be the primary label; append coupon/promotion code when present.
  if ((applyType === DiscountApplyTypeEnum.PERCENTAGE || applyType === "") && percentRule) {
    const rule = percentRule;
    if (reference && discountType === "COUPON") return `${rule} (Coupon ${reference})`;
    if (reference && discountType === "EVENT") return `${rule} (Promotion ${reference})`;
    if (reference) return `${rule} (${reference})`;
    return rule;
  }
  if ((applyType === DiscountApplyTypeEnum.FIXED || applyType === "") && fixedRule) {
    if (reference && discountType === "COUPON") return `${fixedRule} (Coupon ${reference})`;
    if (reference && discountType === "EVENT") return `${fixedRule} (Promotion ${reference})`;
    if (reference) return `${fixedRule} (${reference})`;
    return fixedRule;
  }

  if (reference) {
    if (discountType === "COUPON") return `Coupon (${reference})`;
    if (discountType === "EVENT") return `Promotion (${reference})`;
    return reference;
  }
  if (reason) {
    return reason;
  }
  return "Order Discount";
}

export function formatOrderDiscountValueDisplay(discount: OrderDiscount | null | undefined): string {
  if (discount == null || discount.discountCents == null) return "—";
  const cents = Math.abs(Number(discount.discountCents));
  return `-${formatCentsToDollarsWithDollarSign(cents)}`;
}

/** Backward-compatible display for places that still consume one string. */
export function formatOrderDiscountDisplay(discount: OrderDiscount | null | undefined): string {
  const rule = formatOrderDiscountRuleDisplay(discount);
  const value = formatOrderDiscountValueDisplay(discount);
  if (value === "—") return rule;
  return `${rule} · ${value}`;
}
